import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'umi';
import * as echarts from 'echarts';
import { COLOR_ARR } from '@/pages/dashboard/_config/constant';
import { compare, formatPercentNum } from '@/pages/dashboard/utils/utils';
import { CHART_FONT_FAMILY, DARK_COLOR_ARR, LIGHT_COLOR_ARR } from '../_config/constant';
import { PieChartProps } from '../_config/interface';
import Abnormal from '../../Abnormal';


export default (props: PieChartProps) => {
  const { formatMessage } = useIntl(); // 中英切换方法
  const [cache, setCache] = useState('');
  const ref = useRef(null);
  const [chart, setChart] = useState<echarts.ECharts>(null as any);

  const {
    formatterFunc,
    isDark,
    title,
    legendData,
    colorArr = COLOR_ARR,
    setOtherConfigFunc,
    chartData: propData,
    isShowLegend = true,
    isSort = false,
    isShowTop5Lines = false,
    className = '',
    style = {},
    height,
    isLoading = false,
    isError = false,
    isNodata = false,
  } = props;

  const { chartPercentDecimal } = props?.config || {};

  let chartData = (() => {
    let total = 0;
    propData?.forEach(item => {
      total += (item.value / 1);
    });

    // 为 0 的数据不该占位置
    return propData?.map((item, index) => {
      if (item.value / 1 <= 0) {
        return {
          ...item,
          value: '',
          total
        };
      }
      return {
        ...item,
        total,
        label: {
          show: isShowTop5Lines ? ((index < 5 || item?.name === formatMessage({ id: 'common_others' })) ? true : false) : true,
        }
      };
    });
  })();

  if (isSort) {
    chartData = chartData?.sort(compare("value"));
    let otherIndex;
    chartData.forEach((item, index) => {
      // common_others
      if (item?.name === formatMessage({ id: 'common_others' })) {
        otherIndex = index;
      }
    });
    const otherItem = chartData[otherIndex];
    chartData?.splice(otherIndex, 1);
    chartData.push(otherItem);
  }

  // 设置图形配置
  const makeupOptions = () => {
    const opts = {
      textStyle: {
        fontFamily: CHART_FONT_FAMILY
      },
      legend: {
        type: 'scroll',
        show: isShowLegend,
        selectedMode: true,
        orient: 'horizontal',
        itemHeight: 8,
        itemWidth: 8,
        icon: 'circle',
        bottom: 0,
        itemGap: 18,
        pageIconColor: '#444444', // 翻页下一页的三角按钮颜色
        pageIconInactiveColor: '#888888', // 翻页（即翻页到头时）
        pageButtonPosition: 'end', // start
        data: legendData,
        textStyle: {
          // 图例文字的样式
          color: '#888888',
          fontSize: 12,
        },
      },
      tooltip: {
        trigger: 'item',
        borderWidth: 0,
        borderRadius: 6,
        backgroundColor: 'rgba(255, 255, 255, 0.75)',
        axisPointer: {
          type: 'none',
        },
        confine: true,
        hideDelay: 200,
        triggerOn: 'mousemove',
        padding: 0,
        formatter: formatterFunc,
      },
      color: colorArr ? colorArr : isDark ? DARK_COLOR_ARR : LIGHT_COLOR_ARR,
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '34%',
          style: {
            fill: '#444444',
            fontSize: 12,
            text: title || '',
          },
        },
      ],
      series: [
        {
          name: '',
          type: 'pie',
          minAngle: 5,
          avoidLabelOverlap: true,
          radius: '60%',
          center: ['50%', '50%'],
          labelLine: {
            // 设置延长线的长度
            normal: {
              length: 20, // 设置延长线的长度
            },
          },
          label: {
            alignTo: 'edge',
            formatter: (item) => {
              const { name, data: { total, value } } = item;
              const percent = value / total;
              return `{name|${name}}：{per|${formatPercentNum(percent, chartPercentDecimal ? chartPercentDecimal : 1)}}\n{hr|}\n{a|}`;
            },
            minMargin: 5,
            edgeDistance: 10,
            lineHeight: 15,
            padding: [0, -6],
            rich: {
              a: {
                color: '#999',
                lineHeight: 15, // 设置最后一行空数据高度，为了能让延长线与hr线对接起来
                align: 'center',
              },
              hr: {
                // 设置hr是为了让中间线能够自适应长度
                borderColor: 'auto', // hr的颜色为auto时候会主动显示颜色的
                width: '100%',
                borderWidth: 0.5,
                height: 0.5,
                marginTop: -10,
              },
              per: {
                // 用百分比数据来调整下数字位置，显的好看些。如果不设置，formatter最后一行的空数据就不需要
                padding: [4, 0],
              },
            },
          },
          itemStyle: {
            // 加上  只会出现一种颜色
            normal: {
              color: function (params) {
                const colorList = colorArr ? colorArr : isDark ? DARK_COLOR_ARR : LIGHT_COLOR_ARR;
                return colorList[params.dataIndex];
              },
            },
          },
          data: chartData,
          // startAngle: 90,
          // clockwise: false
        },
      ],
    };
    if (isSort) {
      // 本期只是做overview的效果给产品看，后面如果全局修改则删除，直接写在配置中
      opts.series[0].startAngle = 90;
      opts.series[0].clockwise = false;
    }
    return setOtherConfigFunc ? setOtherConfigFunc(opts) : opts;
  };

  useEffect(() => {
    if (props.chartData?.length <= 0 || isNodata) return;
    renderChart();
  }, [props.chartData, props.legendData, isNodata]);

  // 渲染图形
  const renderChart = () => {
    const options = makeupOptions() || {};
    // 减少 不必要渲染
    if (cache === JSON.stringify(options)) {
      return;
    }
    setCache(JSON.stringify(options));

    const width = (ref.current?.offsetWidth) || 0;
    const resizeOptions = {
      ...(width ? { width } : {}),
    };

    if (!chart) {
      const _chart = echarts.init(ref.current, undefined, {
        renderer: 'canvas',
      });
      _chart.setOption(options);
      window.addEventListener('resize', function () {
        resizeChart(_chart);
      });
      _chart.resize(resizeOptions);
      setChart(_chart);
    } else {
      chart.clear();
      chart.setOption(options);
      chart.resize(resizeOptions);
    }
  };

  const resizeChart = (_chart) => {
    const width = (ref.current?.offsetWidth) || 0;
    const resizeOptions = {
      ...(width ? { width } : {}),
    };
    try {
      _chart.resize(resizeOptions);
    } catch (err) { }
  };

  const chartStyle = {
    ...(height ? { height: `${height}px` } : {}),
    width: '100%',
    ...style
  };

  return (
    <Abnormal
      style={{ height: height ? height : 400 }}
      component={
        <div className={`chart-box ${className}`} style={chartStyle}>
          <div ref={ref} style={chartStyle}></div>
        </div>
      }
      isLoading={isLoading}
      isError={isError}
      isNoData={isNodata}
    />
  );
};
